import React from 'react';

// DS-token pairs per tone. Our logistic web uses Tailwind palette tones
// (amber/sky/rose/emerald/…); here we map each to a DS semantic state token so
// the badge stays on the design system:
//   amber → warning · sky/info → information · rose/danger → error ·
//   success/emerald → success.
type StatusTone = 'warning' | 'success' | 'information' | 'error' | 'neutral';

const TONE_CLASSES: Record<StatusTone, { bg: string; text: string }> = {
  warning: { bg: 'bg-warning-lighter', text: 'text-warning-dark' },
  success: { bg: 'bg-success-lighter', text: 'text-success-dark' },
  information: { bg: 'bg-information-lighter', text: 'text-information-dark' },
  error: { bg: 'bg-error-lighter', text: 'text-error-dark' },
  neutral: { bg: 'bg-bg-weak-50', text: 'text-text-sub-600' },
};

// The canonical invoice statuses (kept in sync with the logistic web's
// STATUS_TONE). Anything the gateway sends that isn't here falls back to a
// neutral, title-cased badge.
const STATUS_CONFIG: Record<string, { label: string; tone: StatusTone }> = {
  PENDING_COLLECTION: { label: 'Pending Collection', tone: 'warning' }, // amber
  RECEIVED_AT_HUB: { label: 'Received at Hub', tone: 'success' }, // success
  RECEIVED_BY_RIDER: { label: 'Received by Rider', tone: 'success' }, // success
  WAITING_CONFIRMATION: { label: 'Waiting Confirmation', tone: 'information' }, // sky
  DISPUTED: { label: 'Disputed', tone: 'error' }, // rose
  RESOLVED: { label: 'Resolved', tone: 'success' }, // emerald
  EXCEPTION: { label: 'Exception', tone: 'error' }, // danger
  COLLECTED: { label: 'Collected', tone: 'information' }, // info
  SUBMITTED_TO_CLIENT: { label: 'Submitted to Client', tone: 'information' }, // info
  CLIENT_ACKNOWLEDGED: { label: 'Client Acknowledged', tone: 'success' }, // emerald
};

const toTitleCase = (status: string) =>
  status
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Human-readable label for an invoice status (reused by the filter).
export const getInvoiceStatusLabel = (status: string): string =>
  STATUS_CONFIG[status]?.label ?? toTitleCase(status || 'Unknown');

// Exception codes the gateway attaches when an invoice can't be collected.
const EXCEPTION_CODE_LABELS: Record<string, string> = {
  OUTLET_NOT_READY: 'Outlet Not Ready',
  OUTLET_CLOSED: 'Outlet Closed',
  OUTLET_DELAYED: 'Outlet Delayed',
  RIDER_TIME: 'Rider Time',
  LEFT_AT_OUTLET: 'Left At Outlet',
  OTHER: 'Other',
};

// Human-readable label for an exception code (falls back to title-case).
export const getInvoiceExceptionLabel = (code: string): string =>
  EXCEPTION_CODE_LABELS[code] ?? toTitleCase(code || 'Unknown');

interface InvoiceStatusBadgeProps {
  status: string;
  className?: string;
}

export const InvoiceStatusBadge: React.FC<InvoiceStatusBadgeProps> = ({
  status,
  className = '',
}) => {
  const config = STATUS_CONFIG[status];
  const label = config?.label ?? toTitleCase(status || 'Unknown');
  const tone = TONE_CLASSES[config?.tone ?? 'neutral'];

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${tone.bg} ${tone.text} ${className}`.trim()}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {label}
    </span>
  );
};

// ─── Posisi Invoice pill ──────────────────────────────────────────────
// The physical position of the surat jalan — a SEPARATE axis from the
// lifecycle Status (a doc can be "Masih di Rider" physically while its status
// is "Exception"). Mirrors the logistic prototype's "Where is it" chip: a
// soft-tint pill with a leading position icon.
type PositionTone =
  | 'neutral'
  | 'away'
  | 'information'
  | 'warning'
  | 'feature';

const POSITION_TONE_CLASSES: Record<
  PositionTone,
  { bg: string; text: string }
> = {
  neutral: { bg: 'bg-bg-weak-50', text: 'text-text-sub-600' },
  away: { bg: 'bg-away-lighter', text: 'text-away-dark' },
  information: { bg: 'bg-information-lighter', text: 'text-information-dark' },
  warning: { bg: 'bg-warning-lighter', text: 'text-warning-dark' },
  feature: { bg: 'bg-feature-lighter', text: 'text-feature-dark' },
};

const POSITION_CONFIG: Record<
  string,
  { label: string; tone: PositionTone; icon: string }
> = {
  PENDING_COLLECTION: { label: 'Belum Scan', tone: 'neutral', icon: 'ri-time-line' },
  RECEIVED_BY_RIDER: { label: 'Masih di Rider', tone: 'away', icon: 'ri-user-line' },
  RECEIVED_AT_HUB: { label: 'Di Hub', tone: 'information', icon: 'ri-building-line' },
  EXCEPTION: { label: 'Di Apotek', tone: 'warning', icon: 'ri-store-2-line' },
  WAITING_CONFIRMATION: { label: 'Di Client', tone: 'feature', icon: 'ri-user-received-line' },
  DISPUTED: { label: 'Di Client', tone: 'feature', icon: 'ri-user-received-line' },
  RESOLVED: { label: 'Di Client', tone: 'feature', icon: 'ri-user-received-line' },
  // Statuses outside the board's canonical lifecycle — mapped for completeness.
  COLLECTED: { label: 'Masih di Rider', tone: 'away', icon: 'ri-user-line' },
  SUBMITTED_TO_CLIENT: { label: 'Di Client', tone: 'feature', icon: 'ri-user-received-line' },
  CLIENT_ACKNOWLEDGED: { label: 'Di Client', tone: 'feature', icon: 'ri-user-received-line' },
};

// Human-readable position label (reused by the detail drawer / filters).
export const getInvoicePositionLabel = (status: string): string =>
  POSITION_CONFIG[status]?.label ?? POSITION_CONFIG.PENDING_COLLECTION.label;

interface InvoicePositionBadgeProps {
  status: string;
  className?: string;
}

export const InvoicePositionBadge: React.FC<InvoicePositionBadgeProps> = ({
  status,
  className = '',
}) => {
  const config = POSITION_CONFIG[status] ?? POSITION_CONFIG.PENDING_COLLECTION;
  const tone = POSITION_TONE_CLASSES[config.tone];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${tone.bg} ${tone.text} ${className}`.trim()}
    >
      <i className={`${config.icon} text-[13px] leading-none`} />
      {config.label}
    </span>
  );
};

// ─── Aging pill ───────────────────────────────────────────────────────
// Mirrors the prototype's agingPill: a "{n}d" chip coloured by tier. We trust
// the gateway's `agingTier` when present, otherwise derive it from daysSince
// (≤3 green · ≤6 amber · ≥7 red).
const AGING_CONFIG: Record<string, { bg: string; text: string }> = {
  green: { bg: 'bg-success-lighter', text: 'text-success-dark' },
  amber: { bg: 'bg-warning-lighter', text: 'text-warning-dark' },
  red: { bg: 'bg-error-lighter', text: 'text-error-base' },
};

const deriveTier = (days: number): string =>
  days <= 3 ? 'green' : days <= 6 ? 'amber' : 'red';

interface InvoiceAgingPillProps {
  days?: number | null;
  tier?: string | null;
  className?: string;
}

export const InvoiceAgingPill: React.FC<InvoiceAgingPillProps> = ({
  days,
  tier,
  className = '',
}) => {
  if (days === null || days === undefined) {
    return <span className="text-text-soft-400">—</span>;
  }
  const resolvedTier = (
    tier && AGING_CONFIG[tier] ? tier : deriveTier(days)
  ) as string;
  const config = AGING_CONFIG[resolvedTier] ?? AGING_CONFIG.green;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${config.bg} ${config.text} ${className}`.trim()}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {days}d
    </span>
  );
};

export default InvoiceStatusBadge;
